/**
 * 接続の許可の画面（/mcp/authorize・pages/McpAuthorize.tsx）を「状態ごとに要る物だけ」にする決まり（純関数）。
 * 2026-10-24 console/consent-simplify（founder 10/24・docs/teams/mgmt/tasks/consent-screen-simplify.md）。
 *
 * WHY: founder が Muse から KUMODeck Preview (v0.2) をつないだとき、許可の画面に「なんか色々出てくるな」。
 *   1 画面に 10 個近い文が並び、「Add permission for Muse?」の見出しの下に「Muse already has this permission. Press Allow to continue.」
 *   （足す物は無い）と「Once connected, …」（初めての接続の文）が同時に出ていた = 言っていることが食い違う。
 *   前は「初めて」か「今の許可がある（stepUpView が null でない）」の 2 つしか分けておらず、今の許可があるときの文は初めての文に足していた。
 * 3 つに分ける（consentState）:
 *   first   … 初めての接続。見出し「Connect {name}?」+ 何ができるかの 1 行（mcpAuth.result.*）
 *   already … もうつながっていて、求められた物はもう全部ある。見出し「{name} is already connected」+ 1 行 + 「Continue」。
 *             「Add permission」とも「Once connected」とも言わない
 *   more    … もうつながっていて、足りない物を足す。見出し「Let {name} also {足す物}?」+ 今できることの 1 行
 * 変えないこと（安全は弱めない・founder の決まり）: 送る許可の中身（approvalBody・stepUpStart の最初の選び方）・プロジェクトの 1 行と「Change」・
 *   行き先（戻り先のホスト）を見える所に出すこと・アカウントの切り替え・Cancel・安全の 1 行。
 * 確かめられないアプリ（インターネット上のサーバー）の強い警告は first だけ。already / more は「同じ本人・同じアプリの今の接続」がある =
 *   本体の existingGrantScopes（apps/server mcpAuthzStepUp.ts）は DCR なら同じ client_id のときだけ返す（DCR の client_id は登録ごとの乱数・
 *   戻り先は登録のときに決まる）= この人が前に同じ警告を見て許可した同じアプリ。行き先の 1 行（mcpAuth.simple.target）は残す。
 *
 * どの接続に使うか（simpleConsentFor）: 今は検証用の MCP（KUMODeck Preview・request.stage === 'staging'）だけ。
 *   WHY: 公開の MCP（kumo-mcp-production）は一覧の審査中で固定（docs/launch/listing-freeze-2026-10-08.md）。この画面はダッシュボードにあり
 *   公開の MCP の接続にも出る = ダッシュボードを出すと公開の接続の許可の画面も変わる。公開の接続へ入れる時期は管理が決める
 *   → そのときは SIMPLE_CONSENT_IN_PRODUCTION を true にする（1 か所）。前の形（公開の接続の今の画面）は McpAuthorize.tsx に残してある。
 * import は mcpAuthorizeStepUp.ts の型だけ（node --test でそのまま動く）
 */

import type { PlainPart } from './mcpAuthorizeStepUp.ts';

export type ConsentState = 'first' | 'already' | 'more';

/**
 * 状態を決める。add = stepUpView の結果（null = 今の許可が無い = 初めて）。
 * 足す物が空（求められた物は今の許可に入っている）なら already
 */
export function consentState(add: { now: readonly PlainPart[]; adding: readonly PlainPart[] } | null): ConsentState {
  if (!add) return 'first';
  return add.adding.length === 0 ? 'already' : 'more';
}

/**
 * 公開の MCP の接続にも新しい形を出すか。false = 検証用の MCP だけ（審査中の固定の間）。
 * true にするのは管理の判断のあと（本番に入れる時期・docs/teams/mgmt/tasks/consent-screen-simplify.md）
 */
export const SIMPLE_CONSENT_IN_PRODUCTION = false;

/** この要求で新しい形を出すか。stage = request.stage（'staging' = 検証用の MCP・無し = 公開の MCP） */
export function simpleConsentFor(stage: string | undefined, inProduction: boolean = SIMPLE_CONSENT_IN_PRODUCTION): boolean {
  return stage === 'staging' || inProduction;
}
